import 'virtual:windi.css'
import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import App from './App.vue'
import router from './router'
import './style.css'
import 'element-plus/dist/index.css'
import ElementPlus from 'element-plus'
import { createHead } from '@vueuse/head'
import { getInitialLanguage, getFinalLanguage } from './utils/geoLocation'
import { initLanguageDebug } from './utils/languageDebug'
import { initImageProtection } from './utils/imageProtection'
import { initFirstPageUrl, initUtmParams } from './utils/utmParams'
import gtmDebugger from './utils/gtmDebug'

// 🏷️ 版本信息 - 构建时自动从 git 获取
declare const __APP_VERSION__: string;
declare const __APP_COMMIT__: string;
declare const __APP_BUILD_TIME__: string;

console.log(
  `%c🚀 Creston Group %c${__APP_VERSION__} %c(${__APP_COMMIT__}) %c${__APP_BUILD_TIME__}`,
  'color: #4CAF50; font-weight: bold; font-size: 14px;',
  'color: #2196F3; font-weight: bold; font-size: 14px;',
  'color: #9C27B0; font-size: 12px;',
  'color: #757575; font-size: 11px;'
);

const head = createHead();

// 🔗 记录用户首次访问的页面 URL 和 UTM 参数（用于表单提交）
initFirstPageUrl();
initUtmParams();

// 初始化调试工具
initLanguageDebug();

// 🔍 开发环境下启用 GTM 调试（可选）
if (import.meta.env.DEV) {
  gtmDebugger.enable();
}

// 尝试动态导入，确保在构建后正确加载
const loadLocales = async () => {
  try {
    const [enModule, vnModule, plModule, itModule] = await Promise.all([
      import('./locales/en'),
      import('./locales/vn'),
      import('./locales/pl'),
      import('./locales/it')
    ])

    // 更严格的模块处理
    let enMessages, vnMessages, plMessages, itMessages

    if (enModule.default) {
      enMessages = enModule.default
    } else if (typeof enModule === 'object' && enModule.home) {
      enMessages = enModule
    } else {
      console.error('❌ Invalid EN module format')
      enMessages = {}
    }

    if (vnModule.default) {
      vnMessages = vnModule.default
    } else if (typeof vnModule === 'object' && vnModule.home) {
      vnMessages = vnModule
    } else {
      console.error('❌ Invalid VN module format')
      vnMessages = {}
    }

    if (plModule.default) {
      plMessages = plModule.default
    } else if (typeof plModule === 'object' && plModule.home) {
      plMessages = plModule
    } else {
      console.error('❌ Invalid PL module format')
      plMessages = {}
    }

    if (itModule.default) {
      itMessages = itModule.default
    } else if (typeof itModule === 'object' && itModule.home) {
      itMessages = itModule
    } else {
      console.error('❌ Invalid IT module format')
      itMessages = {}
    }

    return { enMessages, vnMessages, plMessages, itMessages }
  } catch (error) {
    console.error('❌ Failed to load locales:', error)
    return { enMessages: {}, vnMessages: {}, plMessages: {}, itMessages: {} }
  }
}

const initializeApp = async () => {
  const { enMessages, vnMessages, plMessages, itMessages } = await loadLocales()

  // 确保消息对象不为空
  if (!enMessages || Object.keys(enMessages).length === 0) {
    console.error('❌ EN messages is empty!')
    return
  }

  if (!vnMessages || Object.keys(vnMessages).length === 0) {
    console.error('❌ VN messages is empty!')
    return
  }

  if (!plMessages || Object.keys(plMessages).length === 0) {
    console.error('❌ PL messages is empty!')
    return
  }

  if (!itMessages || Object.keys(itMessages).length === 0) {
    console.error('❌ IT messages is empty!')
    return
  }
  
  // 🚀 立即获取初始语言（无延迟启动）
  const initialLanguage = getInitialLanguage();
  
  const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: initialLanguage, // 使用缓存的语言或默认英语
    fallbackLocale: 'en',
    messages: {
      en: enMessages,
      vn: vnMessages,
      pl: plMessages,
      it: itMessages,
    },
    missingWarn: true,
    fallbackWarn: true,
  })

  const app = createApp(App)
  app.use(i18n)
  app.use(router)
  app.use(ElementPlus)
  app.use(head)
  
  // 挂载到全局便于调试
  window.globalI18n = i18n
  
  app.mount('#app')

  // 🛡️ 启用图片保护（禁止右键保存和拖拽）
  initImageProtection();

  // 🔄 应用启动后，在后台进行语言检测和智能切换
  setTimeout(() => {
    performBackgroundLanguageDetection(i18n);
  }, 1000); // 延迟1秒，确保应用完全加载
}

// 启动应用
initializeApp()

// 后台语言检测函数
const performBackgroundLanguageDetection = async (i18n: any) => {
  try {
    // 检查是否有手动选择的语言
    const hasManualSelection = localStorage.getItem('language_manual_selection') === 'true';
    if (hasManualSelection) {
      return;
    }

    const detectedLanguage = await getFinalLanguage();
    const currentLanguage = i18n.global.locale.value;

    if (detectedLanguage !== currentLanguage) {
      i18n.global.locale.value = detectedLanguage;
      localStorage.setItem('language', detectedLanguage);
    }
  } catch (error) {
    console.error('❌ Background language detection failed:', error);
  }
};
